import { Checkbox, FormSize, TextInput } from '@ardoq/forms';
import { DateOnlyPicker, DateTimePicker } from '@ardoq/date-time-picker';
import {
  formatDateISO,
  formatDateShort,
  formatDateTimeShort,
  parseLocaleDateString,
} from '@ardoq/date-time';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { APITagAttributes, ArdoqId } from '@ardoq/api-types';
import { StatusType } from '@ardoq/status-ui';
import {
  CellSelect,
  CreatableMultiselect,
  Multiselect,
  SelectOptionsOrGroups,
} from '@ardoq/select';
import { isValidTagName } from '@ardoq/validation';
import React, {
  KeyboardEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { columnOperations } from '../columns/columnOperations';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import { CellEditor, CellEditorProps } from './types';
import { cellEditorPropsOperations } from './cellEditorPropsOperations';
import { Box, FlexBox } from '@ardoq/layout';
import { Portal } from '@ardoq/basic-components';
import { Maybe } from '@ardoq/common-helpers';
import {
  ExtendedRowNode,
  InventoryColumnDefinitionWithSelectionState,
  SavingStatus,
} from '../types';
import styled, { css } from 'styled-components';
import { ReferenceEditor } from './ReferenceEditor';
import { useEffectToHideEditorOnHorizontalScrollForPopupEditors } from './useEffectToHideEditorOnHorizontalScrollForPopupEditors';
import { ParentCellEditor } from './ParentCellEditor';
import { NumberCellEditor } from './NumberCellEditor';
import { withPopupCellEditorErrorBoundary } from './CellEditorErrorBoundary';
import { LucidchartEditor } from './LucidchartEditor';
import { DescriptionEditor } from './DescriptionEditor';
import { TextAreaEditor } from './TextAreaEditor';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';

/**
 * Calculate offset for popupParent to fix DateTimePicker positioning in drawers.
 * When the inventory is rendered inside a container (like a drawer) that doesn't
 * fill the viewport (e.g. for Editable Reports), the DateTimePicker needs to
 * adjust its position relative to that container instead of the viewport.
 */
const getPopupParentOffset = (
  popupParent: HTMLElement | null
): { left: number; top: number } | undefined => {
  if (!popupParent) {
    return undefined;
  }
  const popupParentRect = popupParent.getBoundingClientRect();
  return {
    left: -popupParentRect.left,
    top: -popupParentRect.top,
  };
};

// Stops editing and restores focus to the cell — needed for proper handling
// of Escape, Tab and Enter keys in popup editors.
const stopEditingAndRestoreFocus = <T,>(props: CellEditorProps<T>) => {
  props.api.stopEditing();
  props.api.setFocusedCell(
    props.rowIndex,
    props.column,
    props.node.rowPinned ?? null
  );
};

const DateTimeEditor: CellEditor<string> = props => {
  const locale = props.context.config.locale;
  useEffectToHideEditorOnHorizontalScrollForPopupEditors(props.api);

  /**
   * This is a workaround for ag-grid's weird positioning of the dropdown for the pinned rows (new components).
   * We render the dropdown outside of the grid to avoid clipping.
   * Therefore, we need to pass the container element to the DateTimePicker, so that it correctly
   * determines if the input is still focused or if it should close the dropdown.
   *
   * It works in combination with `cellEditorPopup` set to `true` in the column definition for date columns.
   */
  const popupParent = props.api.getGridOption('popupParent');
  const value = cellEditorPropsOperations.getValue(props);
  const additionalDropdownOffset = getPopupParentOffset(popupParent ?? null);

  return (
    <DateTimePicker
      date={value ? parseLocaleDateString(value, locale) : null}
      onChange={date => {
        props.onValueChange(date ? date.toISOString() : null);
      }}
      onCloseDatepicker={() => stopEditingAndRestoreFocus(props)}
      autoFocus
      config={{
        locale,
        useTimePicker: true,
        formatter: formatDateTimeShort,
      }}
      popupParent={popupParent ?? null}
      additionalDropdownOffset={additionalDropdownOffset}
    />
  );
};

const DateOnlyEditor: CellEditor<string> = props => {
  const locale = props.context.config.locale;
  useEffectToHideEditorOnHorizontalScrollForPopupEditors(props.api);
  /**
   * This is a workaround for ag-grid's weird positioning of the dropdown for the pinned rows (new components).
   * We render the dropdown outside of the grid to avoid clipping.
   * Therefore, we need to pass the container element to the DateTimePicker, so that it correctly
   * determines if the input is still focused or if it should close the dropdown.
   *
   * It works in combination with `cellEditorPopup` set to `true` in the column definition for date columns.
   */
  const popupParent = props.api.getGridOption('popupParent');
  const value = cellEditorPropsOperations.getValue(props);
  const additionalDropdownOffset = getPopupParentOffset(popupParent ?? null);

  return (
    <DateOnlyPicker
      date={value ? parseLocaleDateString(value, locale) : null}
      onChange={newDate => {
        props.onValueChange(newDate);
      }}
      onCloseDatepicker={() => stopEditingAndRestoreFocus(props)}
      config={{ locale, formatter: formatDateShort }}
      autoFocus
      popupParent={popupParent ?? null}
      additionalDropdownOffset={additionalDropdownOffset}
    />
  );
};

const CheckBoxEditor: CellEditor<boolean> = props => {
  const value = cellEditorPropsOperations.getValue(props);
  return (
    <FlexBox paddingLeft="medium" height="full" align="center">
      <Checkbox isChecked={value} onChange={props.onValueChange} />
    </FlexBox>
  );
};

const CellSelectEditor = (props: {
  value: Maybe<string>;
  onValueChange: (value: string | null) => void;
  options: SelectOptionsOrGroups<string>;
  isClearable?: boolean;
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(true);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <CellSelect
      {...props}
      ref={ref}
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      formSize={FormSize.FULL}
      isClearable={props.isClearable}
      // Tab belongs to the grid — it commits and moves to the next cell. Left
      // at react-select's default it would also select the focused option as
      // the editor is being torn down.
      tabSelectsValue={false}
    />
  );
};

const getListEditor =
  (column: InventoryColumnDefinitionWithSelectionState): CellEditor<string> =>
  props => {
    const value = cellEditorPropsOperations.getValue(props);
    return (
      <CellSelectEditor
        value={value}
        onValueChange={newValue => {
          props.onValueChange(newValue);
          props.api.stopEditing();
        }}
        options={column.options}
        isClearable={!columnOperations.isUserColumn(column)}
      />
    );
  };

const TypeEditor: CellEditor<string> = props =>
  createComponentsOperations.isComponentNotPersisted(props.data._id)
    ? TypeEditorForUnpersistedComponent(props)
    : TypeEditorForPersistedComponent(props);

const TypeEditorForUnpersistedComponent = (
  props: CellEditorProps<string>
): ReactNode => {
  const locale = props.context.config.locale;
  const value = cellEditorPropsOperations.getValue(props);
  const options =
    cellEditorPropsOperations.getTypeOptionsForAllLoadedWorkspaces(
      props,
      locale
    );

  /**
   * If user selects component type from another workspace, we need to update the component model and workspace.
   * We don't change workspace if there is a component type within selected workspace.
   */
  const onValueChange = (componentTypeId: string | null) => {
    if (!componentTypeId) {
      return;
    }

    const { modelId, workspaceId } =
      cellEditorPropsOperations.getWorkspaceAndModelIdAndComponentTypeIdForSelectedType(
        props,
        componentTypeId
      );

    props.onValueChange(componentTypeId);
    props.node.updateData({
      ...props.data,
      model: modelId,
      typeId: componentTypeId,
      rootWorkspace: workspaceId,
    });
    props.api.stopEditing();
    props.api.refreshCells({ force: true });
  };

  return (
    <CellSelectEditor
      aria-label="Component Type"
      value={value}
      onValueChange={onValueChange}
      options={options}
      isClearable={false}
    />
  );
};

const TypeEditorForPersistedComponent = (
  props: CellEditorProps<string>
): ReactNode => {
  const locale = props.context.config.locale;
  const options = cellEditorPropsOperations.getTypeOptionsForCurrentWorkspace(
    props,
    locale
  );
  const value = cellEditorPropsOperations.getValue(props);
  return (
    <CellSelectEditor
      aria-label="Component Type"
      value={value}
      onValueChange={props.onValueChange}
      options={options}
      isClearable={false}
    />
  );
};

/**
 * Only used for not persisted components.
 */
const WorkspaceEditor: CellEditor<string> = props => {
  const locale = props.context.config.locale;
  const value = cellEditorPropsOperations.getValue(props);
  const options = cellEditorPropsOperations.getWorkspaceOptions(props, locale);

  const onValueChange = (workspaceId: string | null) => {
    if (!workspaceId) {
      return;
    }

    const currentComponentTypeName =
      cellEditorPropsOperations.getComponentTypeNameById(
        props,
        props.data.typeId
      );

    const modelId = enhancedScopeDataOperations.getModelIdByWorkspaceId(
      props.context.scopeData,
      workspaceId
    );

    if (!modelId) {
      return;
    }

    const componentTypesForNewWorkspace = Object.values(
      props.context.scopeData.typesByModelId[modelId].componentTypesById
    );

    const nextComponentTypeId =
      componentTypesForNewWorkspace.find(
        componentType => componentType.name === currentComponentTypeName
      )?.id ?? componentTypesForNewWorkspace[0].id;

    if (!nextComponentTypeId) {
      return;
    }

    props.onValueChange(workspaceId);
    props.node.updateData({
      ...props.data,
      rootWorkspace: workspaceId,
      model: modelId,
      typeId: nextComponentTypeId,
    });
    props.api.stopEditing();
    props.api.refreshCells({ force: true });
  };

  return (
    <CellSelectEditor
      aria-label="Workspace"
      value={value}
      onValueChange={onValueChange}
      options={options}
      isClearable={false}
    />
  );
};

const ComponentNameEditor: CellEditor<string> = props => {
  const value = cellEditorPropsOperations.getValue(props) ?? '';

  const componentNotPersisted =
    createComponentsOperations.isComponentNotPersisted(props.data._id);

  const onValueChange = (name: string) => {
    props.onValueChange(name);

    if (componentNotPersisted) {
      (props.node as ExtendedRowNode)._savingStatus = SavingStatus.PENDING;
    }
  };

  return (
    <Box width="full">
      <TextInput
        isRequired
        aria-label="Component name"
        autoFocus
        value={value}
        onValueChange={onValueChange}
      />
    </Box>
  );
};

const MultiSelectContainer = styled.div<{
  $width: number;
  $left: number;
  $top: number;
}>`
  ${({ $width, $top, $left }) => css`
    width: ${$width}px;
    top: ${$top}px;
    left: ${$left}px;
  `}
  position: absolute;
  z-index: 1;
`;

const getMultiSelectListEditor =
  (column: InventoryColumnDefinitionWithSelectionState): CellEditor<string[]> =>
  props => {
    const value = cellEditorPropsOperations.getValue(props);
    const comboboxKeyboardProps = useMultiValueComboboxKeyboard(props);
    if (cellEditorPropsOperations.isPinnedTopRow(props)) {
      // The issue with pinned rows is that they are in a different stacking context than the rest of the table.
      // This means that the input might get clipped under the rest of the table if too many options are selected.
      // This is fixed by portalling the input and increasing the z-index.
      const { left, top, width } = props.eGridCell.getBoundingClientRect();
      return (
        <Portal to={document.body}>
          <MultiSelectContainer $left={left} $top={top} $width={width}>
            <Multiselect
              value={value}
              onValueChange={props.onValueChange}
              options={column.options}
              isMulti
              autoFocus
              {...comboboxKeyboardProps}
            />
          </MultiSelectContainer>
        </Portal>
      );
    }
    return (
      <Box width="full">
        <Multiselect
          value={value}
          onValueChange={props.onValueChange}
          options={column.options}
          isMulti
          autoFocus
          {...comboboxKeyboardProps}
        />
      </Box>
    );
  };

const toTagOptionsApplicableForEditedComponent = (
  tagsByWorkspaceId: Record<ArdoqId, APITagAttributes[]>,
  workspaceId: ArdoqId
): SelectOptionsOrGroups<string> => {
  const tagsApplicableForEditedComponent = tagsByWorkspaceId[workspaceId] ?? [];
  return tagsApplicableForEditedComponent.map(({ name }) => ({
    label: name,
    value: name,
    statusType: StatusType.INFO,
  }));
};

/**
 * Keyboard contract for the grid's multi-value combobox editors (tags,
 * select-multiple-list), composing the ARIA combobox pattern with the grid's
 * edit mode:
 *
 * - Enter, menu open   → the listbox toggles or creates the focused option
 * - Enter, menu closed → commit the cell, focus back on the cell
 * - Tab                → AG Grid commits and moves to the next cell
 * - Escape             → AG Grid discards the edit, as in every other cell
 *
 * react-select calls this handler before its own and bails out once we have
 * called `preventDefault`, so the two Enter stages can't both fire. The column
 * needs `suppressEnterForMultiValueDropdowns` so AG Grid doesn't act on Enter
 * before the listbox gets it.
 */
const useMultiValueComboboxKeyboard = <T,>(props: CellEditorProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(true);

  const onKeyDown: KeyboardEventHandler = event => {
    if (event.key !== 'Enter' || isMenuOpen) {
      return;
    }
    event.preventDefault();
    stopEditingAndRestoreFocus(props);
  };

  return {
    onKeyDown,
    menuIsOpen: isMenuOpen,
    onMenuOpen: () => setIsMenuOpen(true),
    onMenuClose: () => setIsMenuOpen(false),
    blurInputOnSelect: false,
    tabSelectsValue: false,
  };
};

const TagsEditor: CellEditor<string[]> = props => {
  const value = cellEditorPropsOperations.getValue(props);
  const comboboxKeyboardProps = useMultiValueComboboxKeyboard(props);
  const tagOptionsApplicableForEditedComponent =
    toTagOptionsApplicableForEditedComponent(
      props.context.tagsByWorkspaceId,
      props.data.rootWorkspace
    );

  const newTagHint = (
    <Box padding="small" paddingTop="xsmall">
      <Text variant="text2" color="textSubtle">
        {t(
          props.context.i18nLocale,
          'inventory:editor.newTagHint',
          'Enter a name for your new tag. Alphanumeric characters including "_" and "-" are allowed.'
        )}
      </Text>
    </Box>
  );

  if (cellEditorPropsOperations.isPinnedTopRow(props)) {
    const { left, top, width } = props.eGridCell.getBoundingClientRect();
    return (
      <Portal to={document.body}>
        <MultiSelectContainer $left={left} $top={top} $width={width}>
          <CreatableMultiselect
            value={value}
            onValueChange={props.onValueChange}
            options={tagOptionsApplicableForEditedComponent}
            footerContent={newTagHint}
            // @ts-expect-error issue in @ardoq/select
            isValidNewOption={isValidTagName}
            autoFocus
            {...comboboxKeyboardProps}
          />
        </MultiSelectContainer>
      </Portal>
    );
  }

  return (
    <Box width="full">
      <CreatableMultiselect
        value={value}
        onValueChange={props.onValueChange}
        options={tagOptionsApplicableForEditedComponent}
        footerContent={newTagHint}
        // @ts-expect-error issue in @ardoq/select
        isValidNewOption={isValidTagName}
        autoFocus
        {...comboboxKeyboardProps}
      />
    </Box>
  );
};

const useDateRangeEditorKeyboard = <T,>(props: CellEditorProps<T>) => {
  const startPickerRef = useRef<HTMLDivElement>(null);
  const endPickerRef = useRef<HTMLDivElement>(null);
  const isInternalFocusMove = useRef(false);
  // Tracks whether autoFocus has fired so DateInput remounts (on date change)
  // don't reopen the start calendar.
  const hasAutoFocused = useRef(false);
  const autoFocusStart = !hasAutoFocused.current;
  // Triggers a post-commit focus on the end picker after a start date is selected.
  // Using state + useEffect instead of a synchronous focus call, because a
  // synchronous focus fires focusin while the start picker's hasFocus is still
  // stale-true, causing its onBlur → checkAndSaveDateOrSetError to overwrite
  // the just-selected date with null.
  const [shouldFocusEndPicker, setShouldFocusEndPicker] = useState(false);

  useEffect(() => {
    if (shouldFocusEndPicker) {
      setShouldFocusEndPicker(false);
      endPickerRef.current?.querySelector('input')?.focus();
    }
  }, [shouldFocusEndPicker]);

  const onStartDateChange = (date: Date | null) => {
    hasAutoFocused.current = true;
    if (date !== null) {
      setShouldFocusEndPicker(true);
    }
  };

  const onEndPickerClose = () => {
    if (isInternalFocusMove.current) {
      isInternalFocusMove.current = false;
      return;
    }
    stopEditingAndRestoreFocus(props);
  };

  const handleTabKeyDownCapture = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const inStartPicker = startPickerRef.current?.contains(
      e.target as HTMLElement
    );
    if (inStartPicker && !e.shiftKey) {
      e.stopPropagation();
      endPickerRef.current?.querySelector('input')?.focus();
    } else if (!inStartPicker && e.shiftKey) {
      e.stopPropagation();
      isInternalFocusMove.current = true;
      startPickerRef.current?.querySelector('input')?.focus();
    } else if (inStartPicker && e.shiftKey) {
      e.stopPropagation();
      stopEditingAndRestoreFocus(props);
    }
    // Tab from end picker: no stopPropagation — the event bubbles to
    // closeOnKeyTrigger which saves the typed value, then onEndPickerClose
    // calls stopEditingAndRestoreFocus.
  };

  return {
    startPickerRef,
    endPickerRef,
    autoFocusStart,
    onStartDateChange,
    onEndPickerClose,
    handleTabKeyDownCapture,
  };
};

const DateRangeCellEditor: CellEditor<{
  start: Maybe<string>;
  end: Maybe<string>;
}> = props => {
  const locale = props.context.config.locale;
  useEffectToHideEditorOnHorizontalScrollForPopupEditors(props.api);

  /**
   * This is a workaround for ag-grid's weird positioning of the dropdown for the pinned rows (new components).
   * We render the dropdown outside of the grid to avoid clipping.
   * Therefore, we need to pass the container element to the DateTimePicker, so that it correctly
   * determines if the input is still focused or if it should close the dropdown.
   *
   * It works in combination with `cellEditorPopup` set to `true` in the column definition for date columns.
   */
  const popupParent = props.api.getGridOption('popupParent');
  const value = cellEditorPropsOperations.getValue(props);
  const additionalDropdownOffset = getPopupParentOffset(popupParent ?? null);
  const {
    startPickerRef,
    endPickerRef,
    autoFocusStart,
    onStartDateChange,
    onEndPickerClose,
    handleTabKeyDownCapture,
  } = useDateRangeEditorKeyboard(props);

  const formatDate = (date: Date | null) => {
    if (!date) {
      return null;
    }
    return formatDateISO(date);
  };

  return (
    <div onKeyDownCapture={handleTabKeyDownCapture}>
      <FlexBox gap="xsmall" align="start" backgroundColor="bgDefault">
        <div ref={startPickerRef}>
          <DateTimePicker
            autoFocus={autoFocusStart}
            date={
              value?.start ? parseLocaleDateString(value.start, locale) : null
            }
            onChange={date => {
              onStartDateChange(date);
              props.onValueChange({
                end: value?.end ?? null,
                start: formatDate(date),
              });
            }}
            config={{
              locale,
              formatter: formatDateShort,
              useTimePicker: false,
            }}
            popupParent={popupParent ?? null}
            additionalDropdownOffset={additionalDropdownOffset}
          />
        </div>
        <DateRangeSeparator>-</DateRangeSeparator>
        <div ref={endPickerRef}>
          <DateTimePicker
            date={value?.end ? parseLocaleDateString(value.end, locale) : null}
            onChange={date =>
              props.onValueChange({
                start: value?.start ?? null,
                end: formatDate(date),
              })
            }
            onCloseDatepicker={onEndPickerClose}
            config={{
              locale,
              formatter: formatDateShort,
              useTimePicker: false,
            }}
            popupParent={popupParent ?? null}
            additionalDropdownOffset={additionalDropdownOffset}
          />
        </div>
      </FlexBox>
    </div>
  );
};

const DateTimeRangeCellEditor: CellEditor<{
  start: Maybe<string>;
  end: Maybe<string>;
}> = props => {
  const locale = props.context.config.locale;
  useEffectToHideEditorOnHorizontalScrollForPopupEditors(props.api);

  /**
   * This is a workaround for ag-grid's weird positioning of the dropdown for the pinned rows (new components).
   * We render the dropdown outside of the grid to avoid clipping.
   * Therefore, we need to pass the container element to the DateTimePicker, so that it correctly
   * determines if the input is still focused or if it should close the dropdown.
   *
   * It works in combination with `cellEditorPopup` set to `true` in the column definition for date columns.
   */
  const popupParent = props.api.getGridOption('popupParent');
  const value = cellEditorPropsOperations.getValue(props);
  const additionalDropdownOffset = getPopupParentOffset(popupParent ?? null);
  const {
    startPickerRef,
    endPickerRef,
    autoFocusStart,
    onStartDateChange,
    onEndPickerClose,
    handleTabKeyDownCapture,
  } = useDateRangeEditorKeyboard(props);

  const formatDate = (date: Date | null) => {
    if (!date) {
      return null;
    }
    return date.toISOString();
  };

  return (
    <div onKeyDownCapture={handleTabKeyDownCapture}>
      <FlexBox gap="xsmall" align="start" backgroundColor="bgDefault">
        <div ref={startPickerRef}>
          <DateTimePicker
            autoFocus={autoFocusStart}
            date={
              value?.start ? parseLocaleDateString(value.start, locale) : null
            }
            onChange={date => {
              onStartDateChange(date);
              props.onValueChange({
                end: value?.end ?? null,
                start: formatDate(date),
              });
            }}
            config={{
              locale,
              formatter: formatDateTimeShort,
              useTimePicker: true,
            }}
            popupParent={popupParent ?? null}
            additionalDropdownOffset={additionalDropdownOffset}
          />
        </div>
        <DateRangeSeparator>-</DateRangeSeparator>
        <div ref={endPickerRef}>
          <DateTimePicker
            date={value?.end ? parseLocaleDateString(value.end, locale) : null}
            onChange={date =>
              props.onValueChange({
                start: value?.start ?? null,
                end: formatDate(date),
              })
            }
            onCloseDatepicker={onEndPickerClose}
            config={{
              locale,
              formatter: formatDateTimeShort,
              useTimePicker: true,
            }}
            popupParent={popupParent ?? null}
            additionalDropdownOffset={additionalDropdownOffset}
          />
        </div>
      </FlexBox>
    </div>
  );
};

const DateRangeSeparator = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
`;

/**
 * In ag-Grid, pinned rows are actually rendered in separate containers above and below the main grid body.
 * The problem is: pinned rows have restricted z-index and overflow handling, which means dropdowns
 * (or anything that renders a popup) can get clipped or hidden behind the main rows, because:
 * - Pinned rows don’t have enough z-index compared to the main grid body.
 * - Overflow on the grid containers might be set to hidden, cutting off anything that "spills" out of the pinned row bounds.
 *
 * In order to fix it, we inform AG Grid that this editor needs special care - we use popup editor instead of a standard cell renderer.
 * Popup editors in ag-Grid float outside the normal grid structure.
 */
export const isCellEditorPopup = (
  column: InventoryColumnDefinitionWithSelectionState
) => {
  return (
    columnOperations.isDateColumn(column) ||
    columnOperations.isDateRangeColumn(column) ||
    columnOperations.isReferenceColumn(column) ||
    columnOperations.isDescriptionColumn(column) ||
    columnOperations.isTextAreaColumn(column)
  );
};

export const columnToCellEditor = (
  column: InventoryColumnDefinitionWithSelectionState
): CellEditor<any> | void => {
  if (columnOperations.isTagsColumn(column)) {
    return TagsEditor;
  }
  if (columnOperations.isNameColumn(column)) {
    return ComponentNameEditor;
  }
  if (columnOperations.isCheckBoxColumn(column)) {
    return CheckBoxEditor;
  }
  if (columnOperations.isWorkspaceColumn(column)) {
    return WorkspaceEditor;
  }
  if (columnOperations.isDateTimeColumn(column)) {
    return DateTimeEditor;
  }
  if (columnOperations.isDateOnlyColumn(column)) {
    return DateOnlyEditor;
  }
  if (
    columnOperations.isListColumn(column) ||
    columnOperations.isUserColumn(column)
  ) {
    return getListEditor(column);
  }
  if (columnOperations.isTypeColumn(column)) {
    return TypeEditor;
  }
  if (columnOperations.isDateRangeColumn(column)) {
    return columnOperations.isDateTimeRangeColumn(column)
      ? DateTimeRangeCellEditor
      : DateRangeCellEditor;
  }
  if (columnOperations.isSelectMultipleListColumn(column)) {
    return getMultiSelectListEditor(column);
  }
  if (columnOperations.isReferenceColumn(column)) {
    return withPopupCellEditorErrorBoundary(ReferenceEditor);
  }
  if (columnOperations.isLucidchartColumn(column)) {
    return LucidchartEditor;
  }
  if (columnOperations.isParentColumn(column)) {
    return ParentCellEditor;
  }
  if (columnOperations.isNumberColumn(column)) {
    return NumberCellEditor;
  }
  if (columnOperations.isDescriptionColumn(column)) {
    return withPopupCellEditorErrorBoundary(DescriptionEditor);
  }
  // The description column is a TEXT_AREA field too, so this has to exclude it
  // explicitly rather than rely on the branch above running first.
  if (
    columnOperations.isTextAreaColumn(column) &&
    !columnOperations.isDescriptionColumn(column)
  ) {
    return withPopupCellEditorErrorBoundary(TextAreaEditor);
  }
};
